/**
 * Pantry Utilities
 *
 * Helper functions for normalizing and working with pantry data
 */

/**
 * Normalize pantry data to ensure consistent property names
 * Handles inconsistencies from backend API responses
 *
 * @param {object} pantry - Pantry object from API
 * @returns {object} Normalized pantry object
 */
export function normalizePantryData(pantry) {
  if (!pantry) return null;

  const normalized = { ...pantry };

  // Normalize zipcode/zip_code
  if (!normalized.zip_code && normalized.zipcode) {
    normalized.zip_code = normalized.zipcode;
  } else if (!normalized.zipcode && normalized.zip_code) {
    normalized.zipcode = normalized.zip_code;
  }

  // Normalize name fields
  if (!normalized.name && normalized.pantry_name) {
    normalized.name = normalized.pantry_name;
  } else if (!normalized.pantry_name && normalized.name) {
    normalized.pantry_name = normalized.name;
  }

  // Normalize address fields
  if (!normalized.street && normalized.address_street_1) {
    normalized.street = normalized.address_street_1;
  } else if (!normalized.address_street_1 && normalized.street) {
    normalized.address_street_1 = normalized.street;
  }

  // Normalize photo fields
  if (!normalized.avatar && normalized.small_photo_url) {
    normalized.avatar = normalized.small_photo_url;
  } else if (!normalized.small_photo_url && normalized.avatar) {
    normalized.small_photo_url = normalized.avatar;
  }

  return normalized;
}

/**
 * Get zip code from pantry data (handles both zipcode and zip_code)
 *
 * @param {object} pantry - Pantry object
 * @returns {string|number|null} Zip code or null if not found
 */
export function getZipCode(pantry) {
  if (!pantry) return null;
  return pantry.zip_code || pantry.zipcode || null;
}

/**
 * Get pantry name (handles both name and pantry_name)
 *
 * @param {object} pantry - Pantry object
 * @returns {string|null} Pantry name or null if not found
 */
export function getPantryName(pantry) {
  if (!pantry) return null;
  return pantry.name || pantry.pantry_name || null;
}

/**
 * Get street address (handles both street and address_street_1)
 *
 * @param {object} pantry - Pantry object
 * @returns {string|null} Street address or null if not found
 */
export function getStreetAddress(pantry) {
  if (!pantry) return null;
  return pantry.address_street_1 || pantry.street || null;
}

/**
 * Get pantry photo URL (handles both avatar and small_photo_url)
 *
 * @param {object} pantry - Pantry object
 * @returns {string|null} Photo URL or null if not found
 */
export function getPhotoUrl(pantry) {
  if (!pantry) return null;
  return pantry.avatar || pantry.small_photo_url || null;
}

/**
 * Format full address from pantry data
 *
 * @param {object} pantry - Pantry object
 * @returns {object} Object with street and cityLine properties
 */
export function formatPantryAddress(pantry) {
  if (!pantry) return { street: null, cityLine: null };

  const street = getStreetAddress(pantry);
  const cityStateZip = [];

  if (pantry.city) cityStateZip.push(pantry.city);
  if (pantry.state) cityStateZip.push(pantry.state);

  const zipCode = getZipCode(pantry);
  if (zipCode) cityStateZip.push(zipCode);

  const cityLine = cityStateZip.length > 0 ? cityStateZip.join(', ') : null;

  return { street, cityLine };
}

export default {
  normalizePantryData,
  getZipCode,
  getPantryName,
  getStreetAddress,
  getPhotoUrl,
  formatPantryAddress,
};
